import { useState } from 'react';
import { useRouter } from 'next/router';
import { useReduxState } from 'hooks';
import { useApiData } from 'lib/api';
import {
  getCompetencySummaryFromMergedCompetency,
  getMergedCompetencies
} from 'lib/competencies';
import { attributesConditionsMatch } from 'shared-helpers/customAttributes';
import {
  DisplayFormattedDate,
  EmptyIndicator,
  Pagination,
  ProductLinkWithIcon,
  Search,
  SortBy,
  TitleWithIcon,
  User
} from 'components/common';
import {
  Box,
  BoxOverflowWrapper,
  BoxTable,
  BoxTableCell,
  BoxTableHeader,
  BoxTableRow
} from 'components/common/box';
import {
  ProfileCompetenciesAdd,
  ProfileCompetenciesDetails,
  ProfileCompetenciesEndorsementsActions
} from '.';

import type { RequestUser } from 'shared-types';
import type { GetUsersRes } from 'shared-types/responses';
import type { MergedCompetency } from 'lib/competencies/getMergedCompetencies';

const competenciesPerPage = 25;

interface Props {
  isUserPage: boolean;
  targetUser: RequestUser;
}

const ProfileCompetenciesTable = ({ isUserPage, targetUser }: Props) => {
  const {
    language,
    messages,
    serverConfig,
    user: loggedInUser
  } = useReduxState();

  const router = useRouter();
  const currentPage = router.query.page ? Number(router.query.page) : 1;
  const search = ((router.query.search as string) || '').toLowerCase();
  const sortBy = (router.query.sortBy as string) || 'name';

  const [detailsCompetencyId, setDetailsCompetencyId] = useState<number | null>(
    null
  );

  const {
    data,
    loading,
    updateData: updateCompetencies
  } = useApiData<GetUsersRes>({
    onlyIf: Boolean(targetUser?.id),
    url: `/admin/users?id=${targetUser?.id}`
  });

  const targetUserCompetencies = data?.users?.[0]?.competencies || [];
  const mergedCompetencies = getMergedCompetencies(targetUserCompetencies);

  const getCompetencyName = (competency: MergedCompetency) =>
    competency.nameLocales?.[language] || competency.name || '';

  const getSummaryTime = (
    competency: MergedCompetency,
    field: 'expirationDate' | 'grantedOn'
  ) => {
    const date = getCompetencySummaryFromMergedCompetency({ competency })[
      field
    ];
    return date ? new Date(date).getTime() : null;
  };

  const filteredCompetencies = mergedCompetencies
    .filter((competency) =>
      getCompetencyName(competency).toLowerCase().includes(search)
    )
    .sort((a, b) => {
      if (sortBy === 'granted' || sortBy === 'expires') {
        const field = sortBy === 'granted' ? 'grantedOn' : 'expirationDate';
        const timeA = getSummaryTime(a, field);
        const timeB = getSummaryTime(b, field);

        if (timeA !== timeB) {
          if (timeA === null) return 1;
          if (timeB === null) return -1;
          return sortBy === 'granted' ? timeB - timeA : timeA - timeB;
        }
      }

      return getCompetencyName(a).localeCompare(getCompetencyName(b), language);
    });

  const numberOfPages =
    Math.ceil(filteredCompetencies.length / competenciesPerPage) || 1;
  const competenciesOnPage = filteredCompetencies.slice(
    (currentPage - 1) * competenciesPerPage,
    currentPage * competenciesPerPage
  );

  const detailsCompetency = mergedCompetencies.find(
    (competency) => competency.id === detailsCompetencyId
  );

  const endorsementsEnabled =
    serverConfig.userEnableOtherProfiles?.enableCompetencyEndorsements &&
    attributesConditionsMatch({
      conditions: serverConfig.userEnableOtherProfiles.conditions,
      customAttributeValues: loggedInUser?.customAttributes,
      user: loggedInUser
    }) &&
    attributesConditionsMatch({
      conditions: serverConfig.userEnableOtherProfiles.conditions,
      customAttributeValues: targetUser?.customAttributes,
      user: targetUser
    });

  const loggedInUserHasEditPermissions =
    loggedInUser?.roles.includes('superadmin') ||
    (loggedInUser?.roles.includes('admin') &&
      loggedInUser?.permissions.includes('manage-users'));

  const handlePageChange = ({ page }: { page: number }) => {
    router.replace({ query: { ...router.query, page } }, undefined, {
      shallow: true
    });
  };

  return (
    <>
      <Box
        headerContent={
          <ProfileCompetenciesAdd
            allowOnlyUserEditableCompetencies={!loggedInUserHasEditPermissions}
            excludedCompetencies={targetUserCompetencies}
            targetUserId={targetUser.id}
            updateCompetencies={updateCompetencies}
          />
        }
        headerContentWrap={true}
        loading={loading}
        padding={0}
        subHeaderContent={
          <div
            style={{
              alignItems: 'center',
              display: 'flex',
              flexWrap: 'wrap',
              gap: 10,
              justifyContent: 'space-between',
              width: '100%'
            }}
          >
            <div style={{ minWidth: 200 }}>
              <Search
                placeholder={messages.commonSearch}
                size="header"
                withUrl={true}
              />
            </div>

            <SortBy
              options={[
                { text: messages.commonName, value: 'name' },
                { text: messages.commonGranted, value: 'granted' },
                { text: messages.commonExpires, value: 'expires' }
              ]}
              value={sortBy}
              withUrl={true}
            />
          </div>
        }
        title={messages.analyticsCompetencies}
        titleCount={filteredCompetencies.length}
      >
        {competenciesOnPage.length ? (
          <BoxOverflowWrapper>
            <BoxTable>
              <BoxTableHeader>
                <BoxTableCell>{messages.commonCompetence}</BoxTableCell>
                <BoxTableCell>{messages.commonLevel}</BoxTableCell>
                <BoxTableCell>{messages.commonGranted}</BoxTableCell>
                <BoxTableCell>{messages.commonGrantedBy}</BoxTableCell>
                <BoxTableCell>{messages.commonExpires}</BoxTableCell>
                {endorsementsEnabled ? (
                  <BoxTableCell>
                    {messages.competenciesEndorsements}
                  </BoxTableCell>
                ) : null}
              </BoxTableHeader>

              {competenciesOnPage.map((competency) => {
                const summary = getCompetencySummaryFromMergedCompetency({
                  competency
                });

                return (
                  <BoxTableRow key={competency.id}>
                    <BoxTableCell>
                      <ProductLinkWithIcon
                        competency={{
                          id: competency.id,
                          name: competency.name,
                          nameLocales: competency.nameLocales
                        }}
                        onClick={() => setDetailsCompetencyId(competency.id)}
                      />
                    </BoxTableCell>

                    <BoxTableCell>
                      {competency.highestLevel
                        ? competency.highestLevel.nameLocales?.[language] ||
                          competency.highestLevel.name
                        : null}
                    </BoxTableCell>

                    <BoxTableCell>
                      {summary.grantedOn ? (
                        <DisplayFormattedDate date={summary.grantedOn} />
                      ) : null}
                    </BoxTableCell>

                    <BoxTableCell>
                      {summary.grantors.length
                        ? summary.grantors.map((grantor, index) => (
                            <div
                              key={grantor.id}
                              style={{ marginTop: index > 0 ? 10 : 0 }}
                            >
                              {grantor.type === 'user' && grantor.user ? (
                                <User
                                  enableProfileLink={isUserPage}
                                  showEmail={false}
                                  size="small"
                                  titleStyle={{ fontSize: '1.3rem' }}
                                  user={grantor.user}
                                />
                              ) : (
                                <TitleWithIcon
                                  avatarProps={{
                                    fontSize: '1.2rem',
                                    padding: '6px 0',
                                    size: '26px'
                                  }}
                                  icon={
                                    grantor.type === 'calendar-event'
                                      ? ['far', 'calendar-days']
                                      : grantor.type === 'course'
                                      ? 'book'
                                      : grantor.type === 'path'
                                      ? 'tasks'
                                      : 'check-circle'
                                  }
                                  style={{ alignItems: 'center' }}
                                  title={grantor.title}
                                  titleStyle={{ fontSize: '1.3rem' }}
                                />
                              )}
                            </div>
                          ))
                        : null}
                    </BoxTableCell>

                    <BoxTableCell>
                      {summary.expirationDate ? (
                        <DisplayFormattedDate date={summary.expirationDate} />
                      ) : null}
                    </BoxTableCell>

                    {endorsementsEnabled ? (
                      <BoxTableCell>
                        <ProfileCompetenciesEndorsementsActions
                          competency={competency}
                          renderAsFixed={true}
                          targetUserId={targetUser.id}
                          updateCompetencies={updateCompetencies}
                        />
                      </BoxTableCell>
                    ) : null}
                  </BoxTableRow>
                );
              })}
            </BoxTable>
          </BoxOverflowWrapper>
        ) : (
          <EmptyIndicator icon="medal" title={messages.commonNoCompetencies} />
        )}
      </Box>

      <Pagination
        currentPage={currentPage}
        numberOfPages={numberOfPages}
        onPageChange={handlePageChange}
      />

      {mergedCompetencies.length ? (
        <ProfileCompetenciesDetails
          competency={detailsCompetency || mergedCompetencies[0]}
          enableAdminLinks={isUserPage}
          onHide={() => setDetailsCompetencyId(null)}
          showEndorsements={endorsementsEnabled}
          showLanguageVersionGrantorTitle={!isUserPage}
          targetUserId={targetUser?.id}
          updateCompetencies={updateCompetencies}
          visible={Boolean(detailsCompetency)}
        />
      ) : null}
    </>
  );
};

export default ProfileCompetenciesTable;
